import { useEffect, useState, type FormEvent } from 'react';
import type { ProductInput, ShoppingListEntry, StockEntryInput } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { EmptyState, Modal, storageEmoji, useToast } from '../components/ui.js';
import { EntryForm } from '../components/EntryForm.js';
import {
  EMPTY_PRODUCT_DRAFT,
  productToDraft,
  type ProductDraft,
} from '../components/ProductFields.js';

/** Lista de compras: pendentes, comprados, entrada rapida e limpeza. */
export function ShoppingPage() {
  const { shopping, storages, settings, refresh } = useAppData();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [entryFor, setEntryFor] = useState<ShoppingListEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const pending = shopping.filter((entry) => entry.status === 'pending');
  const purchased = shopping.filter((entry) => entry.status === 'purchased');

  async function run(action: () => Promise<unknown>, message?: string) {
    try {
      await action();
      await refresh();
      if (message) toast(message, 'ok');
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    if (!label.trim()) return;
    const value = label.trim();
    setLabel('');
    await run(() => api.addShopping({ label: value, suggestedQuantity: 1, origin: 'manual' }));
  }

  async function markPurchased(entry: ShoppingListEntry) {
    await run(() => api.updateShopping(entry.id, { status: 'purchased' }));
    // Atalho da regra 6: comprou, ja pode guardar na dispensa.
    if (entry.product) setEntryFor(entry);
  }

  async function saveEntry(input: StockEntryInput, productPatch?: Partial<ProductInput>) {
    setBusy(true);
    try {
      // Correcao feita na hora vale para o produto no catalogo inteiro.
      if (productPatch && input.productId) {
        await api.updateProduct(input.productId, productPatch);
      }
      const item = await api.createEntry(input);
      if (entryFor) await api.removeShopping(entryFor.id);
      setEntryFor(null);
      toast(`${item.product.name} guardado em ${item.storage.name}.`, 'ok');
      await refresh();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  // O rascunho fica em estado porque o formulario agora edita o produto junto
  // com a entrada (marca, tipo, tamanho, codigo de barras).
  const [draft, setDraft] = useState<ProductDraft | null>(null);
  useEffect(() => {
    if (!entryFor) {
      setDraft(null);
      return;
    }
    setDraft(
      entryFor.product
        ? productToDraft(entryFor.product)
        : { ...EMPTY_PRODUCT_DRAFT, name: entryFor.label, productType: entryFor.label },
    );
  }, [entryFor]);

  return (
    <main className="page">
      <div className="page-head">
        <h1>Lista de compras</h1>
        <span className="badge">{pending.length} pendente(s)</span>
        <div className="spacer" />
        {purchased.length > 0 ? (
          <button
            type="button"
            className="btn"
            onClick={() => void run(() => api.clearPurchased(), 'Comprados removidos da lista.')}
          >
            Limpar comprados
          </button>
        ) : null}
      </div>

      <div className="card">
        <form onSubmit={add} className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0, flex: '3 1 240px' }}>
            <label htmlFor="new-item">Adicionar item</label>
            <input
              id="new-item"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Ex.: Papel toalha"
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ flex: '0 0 auto' }}>
            Adicionar
          </button>
        </form>
      </div>

      {pending.length === 0 ? (
        <EmptyState
          icon="🛒"
          title="Nada na lista"
          hint="Quando um item acabar no estoque, ele aparece aqui automaticamente."
        />
      ) : (
        <ul className="list" style={{ marginTop: 16 }}>
          {pending.map((entry) => (
            <li key={entry.id} className="item">
              <input
                type="checkbox"
                style={{ width: 26, height: 26 }}
                aria-label={`Marcar ${entry.label} como comprado`}
                onChange={() => void markPurchased(entry)}
              />
              <div className="info">
                <div className="title">{entry.label}</div>
                <div className="sub">
                  <span className="badge">
                    {entry.suggestedQuantity} {entry.product?.unit ?? 'un'}
                  </span>
                  <span className="badge">
                    {entry.origin === 'auto_depletion' ? 'acabou no estoque' : 'adicionado a mao'}
                  </span>
                </div>
              </div>
              <div className="actions">
                {entry.product ? (
                  <button type="button" className="btn btn-sm" onClick={() => setEntryFor(entry)}>
                    Dar entrada
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => void run(() => api.removeShopping(entry.id))}
                >
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {purchased.length > 0 ? (
        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 18 }}>Comprados</h2>
          <ul className="list">
            {purchased.map((entry) => (
              <li key={entry.id} className="item">
                <span>✅</span>
                <div className="info">
                  <div className="title" style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                    {entry.label}
                  </div>
                </div>
                <div className="actions">
                  {entry.product ? (
                    <button type="button" className="btn btn-sm" onClick={() => setEntryFor(entry)}>
                      Guardar na dispensa
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => void run(() => api.updateShopping(entry.id, { status: 'pending' }))}
                  >
                    Voltar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {draft && entryFor ? (
        <Modal title={`Guardar ${entryFor.label}`} onClose={() => setEntryFor(null)}>
          <p className="small muted" style={{ marginTop: 0 }}>
            Escolha a dispensa de destino — {storages.length} disponivel(is):{' '}
            {storages.map((s) => `${storageEmoji(s.icon)} ${s.name}`).join(', ')}
          </p>
          <EntryForm
            draft={draft}
            onDraftChange={setDraft}
            original={entryFor.product}
            storages={storages}
            defaultStorageId={settings.lastStorageId}
            submitting={busy}
            onSubmit={(input, patch) => void saveEntry(input, patch)}
            onCancel={() => setEntryFor(null)}
          />
        </Modal>
      ) : null}
    </main>
  );
}
